// Sleep analytics — config + extractor. Inherits the shared engine/dashboard
// verbatim (like fitnessDashboard.ts): only WHICH numbers to plot is declared.
//
// Reducers are `avg` (a week with no log is "no data", never "0 hours slept" —
// the engine skips gaps for avg). No streak metric here: the Sleep page already
// shows an engine-canonical streak (SleepStats.streak, computeStreak since the
// foundation PR), so a streak card would duplicate it.
//
// GUARDRAIL (Sleep = no clinical claim): declares only averages/trend. The
// dashboard renders them descriptively — no "you should sleep more" copy.

import type { Sample } from "./engine";
import type { DashboardConfig } from "./dashboard";
import type { SleepLog } from "@/hooks/useSleep";
import { sleepDurationValue, sleepScoreValue } from "@/lib/sleep/values";

export const SLEEP_DASHBOARD: DashboardConfig = {
  moduleKey: "sleep",
  titleKey: "sleep.analytics.title",
  defaultGranularity: "week",
  metrics: [
    {
      key: "duration",
      includeObservationCounts: true,
      labelKey: "sleep.analytics.metric.duration",
      reducer: "avg",
      color: "hsl(220 70% 62%)",
      unitKey: "analytics.unit.h",
      chart: "line",
      projectable: true,
    },
    {
      key: "score",
      includeObservationCounts: true,
      labelKey: "sleep.analytics.metric.score",
      reducer: "avg",
      color: "hsl(187 72% 55%)",
      chart: "line",
      projectable: true,
    },
    {
      key: "debt",
      includeObservationCounts: true,
      labelKey: "sleep.analytics.metric.debt",
      reducer: "avg",
      color: "hsl(35 85% 55%)",
      unitKey: "analytics.unit.min",
      chart: "line",
      projectable: false, // debt is noisy night-to-night — show trend, not a forecast
    },
  ],
};

// sleep_date is a DATE-only column; parse at LOCAL noon so day bucketing never
// drifts across the UTC boundary (same rule the streak migration uses).
function sleepDayMs(sleepDate: string): number {
  return new Date(sleepDate + "T12:00:00").getTime();
}

export function extractSleepSamples(rows: SleepLog[]): Record<string, Sample[]> {
  const duration: Sample[] = [];
  const score: Sample[] = [];
  const debt: Sample[] = [];

  for (const r of rows) {
    if (!r.sleep_date) continue;
    const t = sleepDayMs(r.sleep_date);
    if (!Number.isFinite(t)) continue;
    const category = r.quality ?? undefined;

    const durationValue = sleepDurationValue(r.duration_hours), scoreValue = sleepScoreValue(r.score);
    if (durationValue !== null) duration.push({ t, v: durationValue, category });
    if (scoreValue !== null) score.push({ t, v: scoreValue, category });
    if (durationValue !== null && typeof r.sleep_debt_minutes === "number" && Number.isFinite(r.sleep_debt_minutes)) {
      debt.push({ t, v: r.sleep_debt_minutes, category });
    }
  }

  return { duration, score, debt };
}
